import { useEffect, useState, FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type Me, type FeatureRequest } from '../lib/api';

const CATEGORIES = ['ui', 'workflow', 'integration', 'reporting', 'automation', 'security', 'other'];

function statusPill(s: FeatureRequest['status']) {
  if (s === 'approved' || s === 'shipped') return <span className="pill-ok">{s}</span>;
  if (s === 'rejected' || s === 'withdrawn') return <span className="pill-err">{s}</span>;
  if (s === 'in_progress') return <span className="pill-warn">in progress</span>;
  return <span className="pill-warn">pending review</span>;
}

function scoreBadge(score?: number | null) {
  if (score == null) return null;
  const cls = score >= 70 ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
            : score >= 40 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'
            : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
  return <span className={`pill ${cls}`}>AI feasibility {score}/100</span>;
}

export default function Features({ me }: { me: Me }) {
  const [list, setList] = useState<FeatureRequest[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const focusId = params.get('id');
  const [selected, setSelected] = useState<FeatureRequest | null>(null);

  async function load() {
    try { setErr(null); const r = await api.featuresList({ limit: 50 }); setList(r.features); }
    catch (e: any) { setErr(e.message); }
  }
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (focusId && list) setSelected(list.find((f) => f.request_id === focusId) ?? null);
  }, [focusId, list]);

  async function submit(form: { title: string; description: string; category: string }) {
    setBusy(true); setErr(null);
    try {
      await api.featuresCreate(form);
      setShowForm(false);
      await load();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function decide(req: FeatureRequest, action: 'approve' | 'reject' | 'withdraw') {
    setBusy(true);
    try { await api.featuresDecide(req.request_id, action); await load(); setSelected(null); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Feature requests</h1>
          <p className="text-gray-500 text-sm mt-1">
            Anyone can submit. AI rates feasibility against the live infrastructure. Super-admin approves.
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary">+ New request</button>
      </div>

      {err && <div className="card p-3 text-sm text-tnh-err">{err}</div>}

      {showForm && <NewFeatureForm onSubmit={submit} onCancel={() => setShowForm(false)} busy={busy} />}

      {list === null && <div className="card p-8 text-center text-gray-500">Loading…</div>}
      {list && list.length === 0 && (
        <div className="card p-8 text-center text-gray-500">
          No feature requests yet. Click <strong>New request</strong> above to submit the first one.
        </div>
      )}
      <div className="space-y-3">
        {list?.map((f) => (
          <div
            key={f.request_id}
            onClick={() => setSelected(f)}
            className="card p-4 cursor-pointer hover:border-tnh-accent transition"
          >
            <div className="flex items-center gap-2 flex-wrap">
              {statusPill(f.status)}
              {scoreBadge(f.ai_feasibility_score)}
              <span className="pill bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">{f.category}</span>
              <span className="text-xs text-gray-500">· by {f.created_by_email}</span>
            </div>
            <div className="font-medium mt-2">{f.title}</div>
            {f.ai_summary && <div className="text-sm text-gray-500 mt-1 line-clamp-2">{f.ai_summary}</div>}
          </div>
        ))}
      </div>

      {selected && (
        <FeatureModal me={me} feature={selected} onClose={() => setSelected(null)} onDecide={decide} busy={busy} />
      )}
    </div>
  );
}

function NewFeatureForm({ onSubmit, onCancel, busy }: { onSubmit: (f: { title: string; description: string; category: string }) => void; onCancel: () => void; busy: boolean }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('workflow');
  function handle(e: FormEvent) { e.preventDefault(); onSubmit({ title, description, category }); }
  return (
    <form onSubmit={handle} className="card p-5 space-y-3">
      <div>
        <label className="block text-xs text-gray-500 mb-1">Title</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} required
               className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent focus:outline-none focus:border-tnh-accent"
               placeholder="One-line summary of what you want" />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Description</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={4000} required rows={5}
                  className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent focus:outline-none focus:border-tnh-accent"
                  placeholder="What should this do? Who's it for? Why now?" />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Category</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent focus:outline-none focus:border-tnh-accent">
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
        <button type="submit" disabled={busy} className="btn-primary">{busy ? 'Submitting…' : 'Submit (AI will analyse)'}</button>
      </div>
    </form>
  );
}

function FeatureModal({ me, feature, onClose, onDecide, busy }: { me: Me; feature: FeatureRequest; onClose: () => void; onDecide: (f: FeatureRequest, a: 'approve' | 'reject' | 'withdraw') => void; busy: boolean }) {
  const canDecide = me.role === 'super_admin' && feature.status === 'pending_review';
  const canWithdraw = (feature.created_by_email === me.email || me.role === 'super_admin') && feature.status === 'pending_review';
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="flex gap-2 items-center flex-wrap">{statusPill(feature.status)}{scoreBadge(feature.ai_feasibility_score)}</div>
            <h2 className="font-semibold text-xl mt-2">{feature.title}</h2>
            <div className="text-xs text-gray-500 mt-1">{feature.category} · by {feature.created_by_email} · {new Date(feature.created_at).toLocaleString()}</div>
          </div>
          <button onClick={onClose} className="btn-ghost text-sm">Close</button>
        </div>
        <div className="text-sm whitespace-pre-wrap mb-4">{feature.description}</div>
        {(feature.ai_summary || feature.ai_required_components || feature.ai_estimated_effort || feature.ai_risks) && (
          <div className="p-4 rounded-lg bg-tnh-accent/5 border border-tnh-accent/20 text-sm space-y-2 mb-4">
            <div className="text-[10px] uppercase tracking-wider text-tnh-accent font-semibold">AI analysis</div>
            {feature.ai_summary && <div><b>Summary:</b> {feature.ai_summary}</div>}
            {feature.ai_required_components && <div><b>Required components:</b> {feature.ai_required_components}</div>}
            {feature.ai_estimated_effort && <div><b>Effort estimate:</b> {feature.ai_estimated_effort}</div>}
            {feature.ai_risks && <div><b>Main risk:</b> {feature.ai_risks}</div>}
          </div>
        )}
        {feature.decided_at && (
          <div className="text-sm text-gray-500 mb-4">
            Decided {new Date(feature.decided_at).toLocaleString()} by {feature.decided_by ?? '?'}.
            {feature.decision_notes && <div className="mt-1">Notes: {feature.decision_notes}</div>}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          {canWithdraw && <button onClick={() => onDecide(feature, 'withdraw')} disabled={busy} className="btn-ghost">Withdraw</button>}
          {canDecide && <button onClick={() => onDecide(feature, 'reject')} disabled={busy} className="btn-ghost text-tnh-err">Reject</button>}
          {canDecide && <button onClick={() => onDecide(feature, 'approve')} disabled={busy} className="btn-primary">Approve</button>}
        </div>
      </div>
    </div>
  );
}
